import { motion } from 'motion/react';

import { AIAgentBanner } from '@src/components/AIAgentBanner';
import { ScreenshotGallery } from '@src/components/ScreenshotGallery';
import { Button } from '@src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// Screenshot
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Bug,
  Building2,
  CheckCircle,
  ClipboardList,
  Cpu,
  Crosshair,
  Globe,
  Layers,
  Mail,
  Network,
  Phone,
  Router,
  Shield,
  Smartphone,
  Target,
  Timer,
  XCircle,
} from 'lucide-react';

import aiPrompts from '@src/assets/screenshots/ai-prompts.webp';
import pentestAgents from '@src/assets/screenshots/pentest-agents.webp';
import pentestCreateEngagement from '@src/assets/screenshots/pentest-create-engagement.webp';
import pentestDashboard from '@src/assets/screenshots/pentest-dashboard.webp';
import pentestEngagements from '@src/assets/screenshots/pentest-engagements.webp';
import pentestOperations from '@src/assets/screenshots/pentest-operations.webp';
import pentestReports from '@src/assets/screenshots/pentest-reports.webp';
import pentestScopeItems from '@src/assets/screenshots/pentest-scope-items.webp';
import pentestScopeManagement from '@src/assets/screenshots/pentest-scope-management.webp';
import pentestSessions from '@src/assets/screenshots/pentest-sessions.webp';
import pentestToolStatus from '@src/assets/screenshots/pentest-tool-status.webp';
import pentestWireless from '@src/assets/screenshots/pentest-wireless.webp';
import AnswerSection from '@src/components/seo/AnswerSection';

const BOSSS_BLUE = '#2973a3';
const ORANGE = '#f59e0b';
const BG_COLOR = '#F8FAFC';

// Pain points for Pentest
const painPoints = [
  {
    icon: XCircle,
    problem: 'Scattered Pentest Results',
    description: 'Findings from different assessments live in spreadsheets, PDFs, and emails with no central tracking.',
    solution: 'Centralized engagement management with structured findings, evidence, and remediation tracking.',
  },
  {
    icon: Timer,
    problem: 'No Vulnerability Lifecycle',
    description: 'Critical findings get lost, retesting is ad-hoc, and there\'s no visibility into remediation progress.',
    solution: 'Full vulnerability lifecycle from discovery to verification with automated retest scheduling.',
  },
  {
    icon: AlertTriangle,
    problem: 'Inconsistent Severity Scoring',
    description: 'Different testers use different methods, making it impossible to compare risk across assessments.',
    solution: 'Built-in CVSS v3.1 & v4 calculators with CWE mapping for standardized severity ratings.',
  },
  {
    icon: Layers,
    problem: 'Disconnected Security Testing',
    description: 'Pentest results don\'t flow into vulnerability management or compliance programs.',
    solution: 'Direct integration with GRC for compliance evidence and XDR for threat correlation.',
  },
];

// Assessment types - 8 types
const assessmentTypes = [
  { name: 'Network Vulnerability', icon: Network, desc: 'Network infrastructure testing', color: '#3b82f6' },
  { name: 'Web Application', icon: Globe, desc: 'Web app security testing', color: '#10b981' },
  { name: 'Mobile Application', icon: Smartphone, desc: 'iOS & Android app testing', color: '#8b5cf6' },
  { name: 'API Testing', icon: Cpu, desc: 'REST & GraphQL API testing', color: '#f59e0b' },
  { name: 'Physical Security', icon: Building2, desc: 'Physical access assessment', color: '#ef4444' },
  { name: 'Phishing Campaign', icon: Mail, desc: 'Email social engineering', color: '#ec4899' },
  { name: 'Pretext Calling', icon: Phone, desc: 'Voice social engineering', color: '#06b6d4' },
  { name: 'IoT Assessment', icon: Router, desc: 'Embedded device testing', color: '#84cc16' },
];

// Dashboard metrics
const dashboardMetrics = [
  { label: 'Engagements', value: '12', color: '#3b82f6' },
  { label: 'Active', value: '3', color: '#10b981' },
  { label: 'Findings', value: '58', color: ORANGE },
  { label: 'Critical', value: '14', color: '#ef4444' },
];

// CVSS levels
const cvssLevels = [
  { label: 'None', range: '0.0', color: '#9ca3af' },
  { label: 'Low', range: '0.1-3.9', color: '#22c55e' },
  { label: 'Medium', range: '4.0-6.9', color: '#f59e0b' },
  { label: 'High', range: '7.0-8.9', color: '#f97316' },
  { label: 'Critical', range: '9.0-10.0', color: '#ef4444' },
];

// Common CWEs
const commonCWEs = [
  { id: 'CWE-79', name: 'Cross-Site Scripting (XSS)', color: '#ef4444' },
  { id: 'CWE-89', name: 'SQL Injection', color: '#ef4444' },
  { id: 'CWE-352', name: 'CSRF', color: '#f97316' },
  { id: 'CWE-200', name: 'Information Disclosure', color: '#f59e0b' },
  { id: 'CWE-287', name: 'Improper Authentication', color: '#ef4444' },
];

// Capabilities
const capabilities = [
  {
    icon: ClipboardList,
    title: 'Engagement Management',
    description: 'Track penetration testing engagements from scoping to final report delivery.',
    features: ['Scope definition', 'Rules of Engagement', 'Timeline tracking', 'Report generation'],
  },
  {
    icon: Bug,
    title: 'Finding Tracking',
    description: 'Document vulnerabilities with evidence, CVSS scoring, and remediation guidance.',
    features: ['CVSS v3.1 & v4', 'Evidence attachments', 'CWE mapping', 'Remediation steps'],
  },
  {
    icon: Mail,
    title: 'Social Engineering',
    description: 'Run phishing campaigns and pretext calling exercises with detailed analytics.',
    features: ['Email campaigns', 'Landing pages', 'Click tracking', 'Credential harvesting'],
  },
  {
    icon: Activity,
    title: 'Retest & Verification',
    description: 'Schedule retests and verify remediation with before/after comparison.',
    features: ['Retest scheduling', 'Status tracking', 'Verification evidence', 'Trend analysis'],
  },
];

// Comprehensive tool categories
const toolCategories = [
  {
    name: 'Network Scanning',
    color: '#3b82f6',
    tools: ['Nmap', 'Masscan', 'Rustscan', 'SMB Scanner', 'SNMP', 'FTP Exploit', 'NFS Exploit'],
    description: 'Port scanning, service detection, and protocol exploitation',
  },
  {
    name: 'Web Application',
    color: '#10b981',
    tools: ['ZAP', 'Nikto', 'Nuclei', 'SQLMap', 'Wapiti', 'Gobuster', 'ffuf', 'WPScan', 'WhatWeb', 'wafw00f', 'IDOR Fuzzer', 'SSTI', 'LFI Fuzzer', 'API Fuzzer', 'Upload Bypass', 'Log Poisoning'],
    description: 'Web vulnerability scanning, fuzzing, and web-specific exploits',
  },
  {
    name: 'Reconnaissance',
    color: '#8b5cf6',
    tools: ['Amass', 'Subfinder', 'httpx', 'theHarvester', 'GAU', 'Katana', 'Metagoofil', 'DNSRecon', 'Mail Enumerator'],
    description: 'OSINT, subdomain enumeration, and attack surface discovery',
  },
  {
    name: 'Vulnerability Assessment',
    color: '#f59e0b',
    tools: ['OpenVAS', 'Trivy', 'Semgrep', 'TruffleHog', 'Searchsploit', 'Config Hunter'],
    description: 'Automated vulnerability detection and code analysis',
  },
  {
    name: 'SSL/TLS & Crypto',
    color: '#06b6d4',
    tools: ['testssl.sh', 'sslyze', 'HashID', 'CyberChef', 'Password Cracker', 'GPG Cracker'],
    description: 'Certificate analysis, cipher testing, and cryptography tooling',
  },
  {
    name: 'Cloud & IaC Security',
    color: '#84cc16',
    tools: ['Prowler', 'Scout Suite', 'Checkov', 'KICS', 'Syft', 'Terraform Exploit'],
    description: 'AWS/Azure/GCP auditing and Infrastructure-as-Code scanning',
  },
  {
    name: 'Exploitation',
    color: '#ef4444',
    tools: ['Metasploit', 'Hydra', 'NetExec', 'Impacket', 'Responder', 'BloodHound', 'Bettercap', 'LinPEAS', 'WinPEAS', 'Post-Auth Exploit', 'Kernel Exploit', 'Buffer Overflow', 'DB Exploiter', 'Shell Escape', 'VNC Exploit'],
    description: 'Active exploitation, credential attacks, and post-exploitation',
  },
  {
    name: 'Active Directory',
    color: '#dc2626',
    tools: ['BloodHound', 'Impacket', 'Certipy', 'Kerbrute', 'NetExec', 'Responder', 'Credential Pivot'],
    description: 'AD enumeration, Kerberoasting, and lateral movement',
  },
  {
    name: 'Threat Intelligence',
    color: '#ec4899',
    tools: ['Shodan', 'MISP', 'Cortex', 'Evasion', 'Stego Detector'],
    description: 'IOC analysis, threat enrichment, and evasion techniques',
  },
  {
    name: 'Traffic & Tunneling',
    color: '#0891b2',
    tools: ['mitmproxy', 'Wireshark/tshark', 'Chisel', 'SSHuttle', 'Port Knock Detector'],
    description: 'Network traffic analysis, interception, and tunneling',
  },
  {
    name: 'Social Engineering',
    color: '#7c3aed',
    tools: ['GoPhish', 'Mail Enumerator', 'Pretext Toolkit'],
    description: 'Phishing campaigns and social engineering exercises',
  },
  {
    name: 'Binary & Web Exploit Dev',
    color: '#b45309',
    tools: ['pwntools', 'ROPgadget', 'JWT Tool', 'PHP Injection', 'NoSQL Scanner', 'Node.js Deser', 'Pickle Exploit', 'Django Scanner', 'Next.js Scanner'],
    description: 'Binary exploitation, ROP chains, and language-specific vulns',
  },
];

export function PentestPage() {
  const onNavigateToTop = useNavigateToTop();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-28" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fed7aa 50%, #fdba74 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-20 left-20"
            animate={{ rotate: [0, 10, -10, 0], y: [0, -15, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Crosshair className="w-8 h-8" style={{ color: `${ORANGE}50` }} />
          </motion.div>

          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            className="absolute bottom-40 right-20"
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Bug className="w-6 h-6" style={{ color: `${ORANGE}40` }} />
          </motion.div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div
                style={{ backgroundColor: `${ORANGE}15`, color: ORANGE }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <Crosshair className="w-4 h-4 mr-2" />
                Offensive Security
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Find Vulnerabilities Before Attackers Do
              </h1>

              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                BOSSS Pentest provides comprehensive penetration testing management—from engagement
                scoping to finding remediation—with built-in social engineering capabilities.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {dashboardMetrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="text-center p-3 bg-white/80 rounded-lg shadow-sm"
                  >
                    <div className="text-2xl font-bold" style={{ color: metric.color }}>{metric.value}</div>
                    <div className="text-xs text-gray-600">{metric.label}</div>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    style={{ backgroundColor: ORANGE }}
                    className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl"
                    onClick={() => onNavigateToTop('/demo')}
                  >
                    Schedule a Demo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>

                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    variant="outline"
                    style={{ borderColor: ORANGE, color: ORANGE }}
                    className="px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg"
                    onClick={() => onNavigateToTop('/contact')}
                  >
                    Contact Sales
                  </Button>
                </motion.div>
              </div>
            </motion.div>

            {/* Dashboard Preview */}
            <motion.div
              className="relative"
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white rounded-xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div style={{ backgroundColor: `${ORANGE}15` }} className="w-10 h-10 rounded-lg flex items-center justify-center">
                      <Crosshair className="w-5 h-5" style={{ color: ORANGE }} />
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900">Penetration Testing</h3>
                      <p className="text-xs text-gray-500">Security assessment tracking</p>
                    </div>
                  </div>

                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Active</span>
                </div>

                {/* Scope Breakdown */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-500 mb-2">Scope Breakdown</p>

                  <div className="space-y-2">
                    {[
                      { name: 'Web Applications', count: 8, icon: Globe },
                      { name: 'Mobile Apps', count: 4, icon: Smartphone },
                      { name: 'APIs', count: 6, icon: Cpu },
                      { name: 'Networks', count: 3, icon: Network },
                    ].map((scope, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <scope.icon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700">{scope.name}</span>
                        </div>

                        <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-100 text-blue-700">{scope.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Active Campaigns */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Active Campaigns</p>

                  <div className="space-y-2">
                    {[
                      { name: 'Phishing Campaign', desc: 'Email-based social engineering', color: '#ef4444', icon: Mail },
                      { name: 'Pretext Calling', desc: 'Voice-based social engineering', color: '#8b5cf6', icon: Phone },
                    ].map((campaign, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg border" style={{ borderColor: `${campaign.color}40`, backgroundColor: `${campaign.color}08` }}>
                        <campaign.icon className="w-4 h-4" style={{ color: campaign.color }} />

                        <div className="flex-1">
                          <p className="text-sm font-medium" style={{ color: campaign.color }}>{campaign.name}</p>
                          <p className="text-xs text-gray-500">{campaign.desc}</p>
                        </div>

                        <span style={{ backgroundColor: campaign.color }} className="w-2 h-2 rounded-full animate-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/platform/pentest" />


      {/* Pain Points Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Stop Losing Track of Security Findings
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Traditional pentest management is fragmented and inefficient.
              Here&apos;s how BOSSS Pentest brings order to offensive security.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-red-100">
                        <point.icon className="w-6 h-6 text-red-600" />
                      </div>

                      <div>
                        <CardTitle className="text-xl text-gray-900 mb-2">
                          {point.problem}
                        </CardTitle>

                        <p className="text-sm text-gray-500">{point.description}</p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <div style={{ backgroundColor: `${ORANGE}08` }} className="flex items-start gap-3 p-4 rounded-lg">
                      <CheckCircle style={{ color: ORANGE }} className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-700">{point.solution}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Screenshot Section */}
      <section className="py-20 bg-gradient-to-b from-white to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive penetration testing management with findings tracking, severity scoring, and remediation workflows.
            </p>
          </motion.div>

          {/* Screenshot Tabs */}
          <ScreenshotGallery
            urlPath="pentest"
            accentColor={ORANGE}
            screenshots={[
              { src: pentestDashboard, label: 'Dashboard', alt: 'BOSSS Pentest dashboard with engagement counts, finding severity and remediation status' },
              { src: pentestOperations, label: 'Operations', alt: 'BOSSS Pentest operations dashboard with real-time vulnerability metrics, severity breakdown and job analytics' },
              { src: pentestToolStatus, label: 'Tool Status', alt: 'BOSSS Pentest tool availability across 125 tools with live online status and response time, each mapped to its attack phases, execution method and Docker container' },
              { src: pentestAgents, label: 'AI Agents', alt: 'BOSSS autonomous pentest agents with live job logs against network, wireless, cloud and infrastructure-as-code targets' },
              { src: aiPrompts, label: 'AI Red-Team', alt: 'BOSSS corpus of 107 adversarial AI prompts covering jailbreak, multi-turn erosion, prompt injection and PII extraction, each referenced to the OWASP LLM Top 10' },
              { src: pentestEngagements, label: 'Engagements', alt: 'BOSSS Pentest engagement tracking with progress against estimated hours' },
              { src: pentestCreateEngagement, label: 'New Engagement', alt: 'BOSSS Pentest engagement wizard stepping through basic details, scope types, scope configuration, rules of engagement and review' },
              { src: pentestScopeManagement, label: 'Scope Management', alt: 'BOSSS Pentest scope management across every engagement, broken down by web app, network, physical, phishing, pretext, embedded, cloud and LLM target type' },
              { src: pentestScopeItems, label: 'Scope Items', alt: 'BOSSS Pentest scope items listing each target with its client and owning engagement' },
              { src: pentestSessions, label: 'Sessions', alt: 'BOSSS Pentest Metasploit sessions with Meterpreter and shell access to Windows hosts, showing the captured account, session duration and the linked scan job for each' },
              { src: pentestWireless, label: 'Wireless Node', alt: 'BOSSS Pentest wireless access point node with device configuration, SSH access, keypair relay, physical location and authorization status' },
              { src: pentestReports, label: 'Reports', alt: 'BOSSS Pentest engagement reports ready for client delivery' },
            ]}
          />

          {/* Feature Callouts */}
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
          >
            {[
              { icon: Crosshair, label: 'Assessment Types', value: '8 types' },
              { icon: Bug, label: 'Finding Tracking', value: 'Full lifecycle' },
              { icon: Target, label: 'Severity Scoring', value: 'CVSS-based' },
              { icon: Activity, label: 'Remediation', value: 'Workflow-driven' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-white rounded-xl shadow-md border border-gray-100">
                <item.icon style={{ color: ORANGE }} className="w-6 h-6 mx-auto mb-2" />
                <p className="text-lg font-bold text-gray-900">{item.value}</p>
                <p className="text-xs text-gray-500">{item.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 8 Assessment Types Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              8 Assessment Types
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive coverage across your entire attack surface—from networks to social engineering.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {assessmentTypes.map((type, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-lg transition-all group">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div style={{ backgroundColor: `${type.color}15` }} className="w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <type.icon className="w-5 h-5" style={{ color: type.color }} />
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm group-hover:text-orange-600 transition-colors">{type.name}</h4>
                        <p className="text-xs text-gray-500">{type.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CVSS & CWE Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Industry-Standard Scoring
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built-in CVSS calculators and CWE mapping ensure consistent, comparable vulnerability ratings.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* CVSS Calculator Preview */}
            <motion.div
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <Card className="p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="w-5 h-5" style={{ color: ORANGE }} />
                  <h4 className="font-semibold text-gray-900">CVSS v3.1 / v4 Calculator</h4>
                  <span style={{ color: '#ef4444' }} className="ml-auto text-2xl font-bold">9.8</span>
                </div>

                <div className="bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 h-2 rounded-full mb-6" />

                <div className="grid grid-cols-2 gap-3 text-sm">
                  {cvssLevels.map((level, i) => (
                    <div key={i} style={{ backgroundColor: `${level.color}10` }} className="flex items-center justify-between p-2 rounded">
                      <span className="font-medium" style={{ color: level.color }}>{level.label}</span>
                      <span className="text-xs text-gray-500">{level.range}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>

            {/* Common CWEs */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <Card className="p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <Bug className="w-5 h-5" style={{ color: ORANGE }} />
                  <h4 className="font-semibold text-gray-900">CWE References</h4>
                </div>

                <div className="space-y-2">
                  {commonCWEs.map((cwe, i) => (
                    <motion.div
                      key={i}
                      viewport={{ once: true }}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                      className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:shadow-sm transition-all"
                    >
                      <span className="px-2 py-1 rounded text-xs font-mono font-medium" style={{ backgroundColor: `${cwe.color}15`, color: cwe.color }}>
                        {cwe.id}
                      </span>

                      <span className="text-sm text-gray-700">{cwe.name}</span>
                    </motion.div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comprehensive Security Tools Arsenal */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div
              style={{ backgroundColor: `${ORANGE}15`, color: ORANGE }}
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <Target className="w-4 h-4 mr-2" />
              105+ Integrated Security Tools
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Complete Security Testing Arsenal
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From reconnaissance to exploitation, every tool you need for comprehensive security assessments—all orchestrated through AI voice commands.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {toolCategories.map((category, index) => (
              <motion.div
                key={category.name}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-all duration-300 border-2 hover:border-orange-200">
                  <CardHeader>
                    <div
                      style={{ backgroundColor: `${category.color}15` }}
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                    >
                      <Crosshair className="w-5 h-5" style={{ color: category.color }} />
                    </div>

                    <CardTitle className="text-base font-semibold text-gray-900">
                      {category.name}
                    </CardTitle>

                    <p className="text-xs text-gray-500 mt-1">{category.description}</p>
                  </CardHeader>

                  <CardContent>
                    <div className="flex flex-wrap gap-1.5">
                      {category.tools.map((tool, i) => (
                        <span
                          key={i}
                          className="text-xs px-2 py-1 rounded-md font-medium transition-all duration-200 hover:scale-105"
                          style={{
                            backgroundColor: `${category.color}10`,
                            color: category.color,
                            border: `1px solid ${category.color}30`,
                          }}
                        >
                          {tool}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            viewport={{ once: true }}
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-md border border-orange-100">
              <CheckCircle className="w-5 h-5" style={{ color: ORANGE }} />
              <span className="text-sm font-semibold text-gray-700">All tools containerized for security</span>
              <span className="text-gray-400">•</span>
              <span className="text-sm font-semibold text-gray-700">Automated result parsing</span>
              <span className="text-gray-400">•</span>
              <span className="text-sm font-semibold text-gray-700">AI-powered orchestration</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 16-Phase Automated Workflow Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6 bg-orange-500/20 text-orange-400 border border-orange-500/30">
              <Layers className="w-4 h-4 mr-2" />
              16-Phase Automated Workflow
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Complete Attack Lifecycle Automation
            </h2>

            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              From AI-powered reconnaissance to social engineering—a comprehensive security assessment workflow executed through voice commands and intelligent orchestration.
            </p>
          </motion.div>

          {/* Workflow Phases Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-12">
            {[
              { phase: '0', name: 'AI Recon', desc: 'httpx, whatweb, wafw00f', color: '#6366f1' },
              { phase: '1', name: 'Reconnaissance', desc: 'nmap, subfinder, theharvester', color: '#8b5cf6' },
              { phase: '2', name: 'Discovery', desc: 'ffuf, gobuster, masscan', color: '#a855f7' },
              { phase: '3', name: 'Web Scanning', desc: 'zap, nuclei, nikto', color: '#d946ef' },
              { phase: '4', name: 'Vuln Assessment', desc: 'openvas, trivy', color: '#ec4899' },
              { phase: '5', name: 'SSL Testing', desc: 'testssl, sslyze', color: '#f43f5e' },
              { phase: '6', name: 'Threat Intel', desc: 'shodan, misp, cortex', color: '#ef4444' },
              { phase: '7', name: 'Security Analysis', desc: 'semgrep, trufflehog', color: '#f97316' },
              { phase: '8', name: 'Exploitation', desc: 'metasploit, sqlmap', color: '#fb923c' },
              { phase: '9', name: 'Credential Attack', desc: 'hydra, netexec', color: '#fbbf24' },
              { phase: '10', name: 'Active Directory', desc: 'bloodhound, impacket', color: '#facc15' },
              { phase: '11', name: 'Post Exploit', desc: 'linpeas, meterpreter', color: '#a3e635' },
              { phase: '12', name: 'Password Crack', desc: 'hashcat, john', color: '#4ade80' },
              { phase: '13', name: 'Cloud Security', desc: 'prowler, scout suite', color: '#22c55e' },
              { phase: '14', name: 'Traffic Analysis', desc: 'mitmproxy, wireshark', color: '#10b981' },
              { phase: '15', name: 'Social Eng.', desc: 'gophish, pretext', color: '#14b8a6' },
            ].map((item, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: i * 0.03 }}
                className="bg-slate-800/50 rounded-lg p-3 border border-slate-700 hover:border-orange-500/50 transition-all group"
              >
                <div
                  style={{ backgroundColor: item.color }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center mb-2 text-xs font-bold text-white mx-auto"
                >
                  {item.phase}
                </div>

                <h4 className="text-xs font-semibold text-white text-center group-hover:text-orange-400 transition-colors">{item.name}</h4>
                <p className="text-[10px] text-slate-500 text-center mt-1 truncate">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Attack Chain Types */}
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="bg-slate-800/50 rounded-xl p-8 border border-slate-700"
          >
            <h3 className="text-xl font-bold text-white text-center mb-6">25 Pre-Built Attack Chains</h3>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {[
                { name: 'Quick Scan', time: '~25 min', tools: 'zap, openvas, shodan' },
                { name: 'Standard', time: '~60 min', tools: 'nmap, nuclei, openvas' },
                { name: 'Comprehensive', time: '~120 min', tools: '17+ tools full workflow' },
                { name: 'Web App', time: '~60 min', tools: 'httpx, zap, sqlmap' },
                { name: 'Network', time: '~90 min', tools: 'nmap, openvas, shodan' },
                { name: 'AD Pentest', time: '~90 min', tools: 'bloodhound, impacket' },
                { name: 'Cloud (AWS)', time: '~45 min', tools: 'prowler, checkov' },
                { name: 'DevSecOps', time: '~30 min', tools: 'semgrep, trivy, syft' },
                { name: 'WordPress', time: '~30 min', tools: 'wpscan, nuclei' },
                { name: 'Phishing', time: '~30 min', tools: 'gophish, theharvester' },
              ].map((chain, i) => (
                <div key={i} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700 hover:border-orange-500/30 transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-white">{chain.name}</span>
                    <span className="text-xs text-orange-400 font-medium">{chain.time}</span>
                  </div>

                  <p className="text-xs text-slate-500">{chain.tools}</p>
                </div>
              ))}
            </div>

            <p className="text-center text-slate-500 text-sm mt-6">
              + 15 more specialized attack chains including IaC scanning, SSL audits, credential attacks, and social engineering
            </p>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20" style={{ backgroundColor: BG_COLOR }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Complete Pentest Management
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to manage penetration testing engagements from start to finish.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 group">
                  <CardHeader className="pb-2">
                    <div
                      style={{ backgroundColor: `${ORANGE}15` }}
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    >
                      <capability.icon className="w-6 h-6" style={{ color: ORANGE }} />
                    </div>

                    <CardTitle className="text-lg group-hover:text-orange-600 transition-colors">
                      {capability.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{capability.description}</p>

                    <ul className="space-y-1">
                      {capability.features.map((feature, i) => (
                        <li key={i} className="flex items-center text-xs text-gray-500">
                          <CheckCircle className="w-3 h-3 mr-2" style={{ color: ORANGE }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Agent Banner */}
      <AIAgentBanner
        toolsCount={127}
        accentColor="#f97316"
        agentName="PenTester Agent"
        exampleQueries={[
          '"Run a comprehensive vulnerability scan on example.com"',
          '"Check for exposed ports and services on our network"',
          '"Scan for OWASP Top 10 vulnerabilities"',
          '"Launch Nuclei templates for CVE-2024 vulnerabilities"',
        ]}
        description={
          'The most powerful AI agent — 127 integrated security tools across 76+ Docker containers in an 18-phase automated workflow with 42 pre-built scan types. Includes Nmap, Metasploit, Nuclei, OWASP ZAP, WPScan, BloodHound, '
          + 'Shodan, MobSF, PyRIT, Garak, and more — all controlled through voice commands.'
        }
      />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Level Up Your Security Testing?
            </h2>

            <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
              See how BOSSS Pentest can streamline your offensive security program
              and help you find vulnerabilities before attackers do.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  className="bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                  onClick={() => onNavigateToTop('/demo')}
                >
                  Schedule a Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>

              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  variant="outline"
                  className="px-8 py-4 text-lg rounded-lg transition-all duration-200 border-white/80 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm"
                  onClick={() => onNavigateToTop('/contact')}
                >
                  Talk to Sales
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Building2, value: '8+', label: 'Assessment Types' },
              { icon: Bug, value: 'Unlimited', label: 'Findings Tracking' },
              { icon: Target, value: 'Built-In', label: 'Risk Scoring' },
              { icon: Shield, value: 'Automated', label: 'Remediation' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <stat.icon style={{ color: BOSSS_BLUE }} className="w-8 h-8 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default PentestPage;
